import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import classnames from 'classnames';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { Button } from 'ui-components/components/button';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalFrameworkMethodsPropType } from 'ui-components/prop-types/modal-framework-methods';

import BulkAction from '../../../components/bulk-action';
import { serviceDropdownHasErrors } from '../../../components/bulk-action/bulk-actions/shipping-service';
import { validateDutiesAndTaxes } from '../../../components/bulk-action/bulk-actions/duties-and-taxes';
import { BulkApplyMessage } from '../../../components/bulk-apply-message';
import { BulkActions } from '../../../enums/bulk-actions';
import { usePrevious } from '../../../hooks/use-previous';
import { optionsToValidate } from '../../../services/other-shipping-options/constants';
import style from './bulk-update-modal.module.less';
import Global from '../../../store/global-selectors';
import * as BulkUpdateActions from '../../../store/bulk/update/bulk.update.actions';
import { BulkFulfillmentPlanTypeEnum } from '../../../enums/bulk-fulfillment-plan-type';
import { messages } from './messages';
import { getErrorsForAction, renderErrors } from './utils';
import { BulkActionJobValidator, BulkActionJobErrorType } from '../../../services/bulk-action-job-validator';
import { useRateShopper } from '../../../pages/settings/rate-shopper/hooks/use-rate-shopper';
import { useMultiPackageShipments } from '../../../feature-flags/hooks/use-multi-package-shipments';

const bulkActionIdToModalClassMap = {
  [BulkActions.otherShippingOptions.name]: style['other-shipping-options'],
};

export const BulkUpdateModal = ({ modalFrameworkMethods, bulkUpdateJobId }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const job = useSelector((state) => Global.selectBulkUpdateBulkUpdateJob(state, { bulkUpdateJobId }));
  const { bulkUpdateTypeData, model, bulkActions, jobData = {}, isLoading } = job;
  const count = jobData.fulfillmentPlanCount;
  const bulkAction = bulkActions[0]; // this is the bulkAction object
  const errors = getErrorsForAction(model, bulkAction.name);
  jobData.bulkUpdateJobId = bulkUpdateJobId;
  const enableMultiPackageShipments = useMultiPackageShipments();

  const { ignoreConfirmationBulkUpdate, ignorePackageBulkUpdate } = useRateShopper();
  const [bulkActionInlineMessage, setBulkActionInlineMessage] = useState(null);

  const updateSelection = useCallback(() => {
    dispatch(BulkUpdateActions.validateBulkUpdateJob(bulkUpdateJobId));
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const { cancelModal } = modalFrameworkMethods;
  const { pathname } = useLocation();
  const prevPathname = usePrevious(pathname);

  useEffect(() => {
    BulkActionJobValidator.setConfig({ enableMultiPackageShipments });
    BulkActionJobValidator.validate(
      bulkActions,
      jobData,
      { ignoreConfirmationBulkUpdate, ignorePackageBulkUpdate },
      BulkActionJobErrorType.InlineError,
    )
      .then(() => setBulkActionInlineMessage(null))
      .catch(() => {
        const numberOfAffectedShipments =
          parseInt(jobData.fulfillmentPlanCount, 10) - parseInt(jobData.bulkFulfillmentPlanShippingStrategiesCount, 10);
        if (numberOfAffectedShipments === 0) {
          cancelModal();
        }
        const parsedErrorMessage = {
          header: (
            <FormattedMessage
              {...messages.bulkEditErrorTitle}
              values={{
                numberOfShipments: numberOfAffectedShipments,
                numberOfTotalShipments: jobData.fulfillmentPlanCount,
              }}
            />
          ),
          content: <FormattedMessage {...messages.bulkEditErrorDescription} />,
        };

        setBulkActionInlineMessage(parsedErrorMessage);
      });
  }, [bulkActions, jobData, ignoreConfirmationBulkUpdate, ignorePackageBulkUpdate, cancelModal, enableMultiPackageShipments]);

  const handleChange = useCallback(
    (newModel) => {
      dispatch(BulkUpdateActions.changeModel(bulkUpdateJobId, newModel));
    },
    [bulkUpdateJobId, dispatch],
  );

  const [isValid, setIsValid] = useState(true);

  const nestedShippingOptions = (obj) => {
    return Object.values(obj).filter((val) => !!val && typeof val === 'object');
  };

  const validate = (values) => {
    const conditions = ['0', '', 'NaN', undefined];
    return conditions.some((el) => values.includes(el));
  };

  const { options } = model?.shippingConfiguration ?? {};

  const actionsNotAllowedWithMultiplePackages = useMemo(
    () => [BulkActions.insurance.name, BulkActions.weight.name, BulkActions.size.name, BulkActions.adjustWeight.name],
    [],
  );

  useEffect(() => {
    if (options) {
      const validations = [];
      optionsToValidate.forEach((optionName) => {
        const value = options[optionName];
        if (!value) {
          return;
        }
        if (typeof value === 'string') {
          validations.push(!validate([value]));
        }
        if (typeof value === 'object' && value !== null) {
          const optionValues = Object.values(value);
          // Some shipping options have nested objects. Looping through once to get the values.
          // dryIce: { weight: {unit: "Ounces", value: "0"} }
          if (optionValues.some((val) => typeof val === 'object' && val !== null)) {
            nestedShippingOptions(optionValues).forEach((val) => {
              validations.push(!validate(Object.values(val)));
            });
          } else {
            validations.push(!validate(optionValues));
          }
        }
      });
      setIsValid(!validations.includes(false));
    }
    if (bulkAction?.name === BulkActions.service.name) {
      const hasErrors = serviceDropdownHasErrors(jobData?.bulkFulfillmentType, jobData?.sellerFulfillmentProviderId);
      setIsValid(!hasErrors);
      if (jobData?.bulkFulfillmentPlanType === BulkFulfillmentPlanTypeEnum.Mixed) {
        cancelModal();
      }
    }
    if (bulkAction?.name === BulkActions.dutiesAndTaxes.name) {
      setIsValid(validateDutiesAndTaxes(jobData));
    }

    if (
      bulkAction?.name === BulkActions.customsDeclarations.name &&
      jobData?.bulkFulfillmentPlanType === BulkFulfillmentPlanTypeEnum.Domestic
    ) {
      cancelModal();
    }
    if (enableMultiPackageShipments && actionsNotAllowedWithMultiplePackages.includes(bulkAction?.name) && jobData?.hasMultiplePackages) {
      cancelModal();
    }
  }, [
    bulkAction?.name,
    cancelModal,
    jobData,
    jobData?.bulkFulfillmentType,
    jobData?.sellerFulfillmentProviderId,
    options,
    actionsNotAllowedWithMultiplePackages,
    enableMultiPackageShipments,
  ]);

  // Close BulkUpdateModal when user clicks on the Ship From drop-down error message
  useEffect(() => {
    if (prevPathname && prevPathname !== pathname) {
      cancelModal();
    }
  }, [cancelModal, pathname, prevPathname]);

  if (!bulkAction) {
    return null;
  }
  const actionTranslation = <FormattedMessage {...bulkAction.message} />;

  return (
    <Modal className={classnames(style['bulk-update-modal'], bulkActionIdToModalClassMap[bulkAction.name] || '')}>
      <ModalHeader closeModal={cancelModal}>
        <FormattedMessage {...messages.header} values={{ action: actionTranslation }} />
      </ModalHeader>
      <ModalBody>
        {!isLoading && (
          <div>
            <BulkApplyMessage bulkUpdateTypeData={bulkUpdateTypeData} count={count} customMessage={bulkActionInlineMessage} />
            {renderErrors(errors)}
            <div className={style['main-content']}>
              <BulkAction bulkActionId={bulkAction.name} jobData={jobData} model={model} onChange={handleChange} onClose={cancelModal} />
            </div>
          </div>
        )}
        {isLoading && <LoadingIndicator className={style['loading-indicator']} iconTitle={intl.formatMessage(messages.loadingTitle)} />}
      </ModalBody>
      {!isLoading && (
        <ModalFooter className={style['modal-footer']}>
          <Button onClick={cancelModal} theme='tertiary'>
            <FormattedMessage {...messages.cancelButton} />
          </Button>
          <Button disabled={!isValid} onClick={() => updateSelection()} theme='primary'>
            <FormattedMessage {...messages.updateSelectionButton} />
          </Button>
        </ModalFooter>
      )}
    </Modal>
  );
};

BulkUpdateModal.propTypes = {
  modalFrameworkMethods: ModalFrameworkMethodsPropType.isRequired,
  bulkUpdateJobId: PropTypes.string.isRequired,
};
